import React, { useCallback, useEffect, useState } from 'react';
import {
  AdminReviewApiError,
  fetchMotionClaimReportInsertionCandidates,
  fetchMotionReviewNotesPreview,
  normalizeReviewerId,
  publishMotionReviewNoteToReport,
  revokeMotionReviewNoteFromReport,
  type MotionReportInsertionCandidate,
  type MotionReviewNotesPreviewResponse,
} from '../../lib/adminReviewApi';

export interface MotionReviewNotesDisplayItem {
  window_id: string;
  statement: string;
  concept_display: string;
  attack_area_display: string;
  published_to_report: boolean;
  visible_in_motion_review_notes: boolean;
  insertion_status: string;
}

export interface MotionReviewNotesPreviewPanelViewProps {
  uploadId: string;
  payload: MotionReviewNotesPreviewResponse | null;
  displayItems: MotionReviewNotesDisplayItem[];
  loading: boolean;
  error: string | null;
  insertedBy: string;
  onInsertedByChange: (value: string) => void;
  actionLoadingWindowId?: string | null;
  cardSuccessMessages?: Record<string, string>;
  cardErrorMessages?: Record<string, string>;
  onPublishToReportWeb: (windowId: string) => void;
  onRevokeFromReportWeb: (windowId: string) => void;
}

const InternalOnlyBadge: React.FC = () => (
  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
    Internal only
  </span>
);

const NotPublishedBadge: React.FC = () => (
  <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-900 dark:bg-red-900/40 dark:text-red-200">
    Not published
  </span>
);

const AdminPreviewBadge: React.FC = () => (
  <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-200">
    Admin preview
  </span>
);

export function reportWebStatusLabel(status?: string): string {
  if (status === 'customer_report_inserted') {
    return 'Inserted to Motion Review Notes web report';
  }
  if (status === 'customer_report_revoked') {
    return 'Revoked from Motion Review Notes';
  }
  return 'Not inserted to customer report';
}

export function buildMotionReviewNotesDisplayItems(
  payload: MotionReviewNotesPreviewResponse,
  reportWebRecordsByWindow: Record<string, MotionReportInsertionCandidate>
): MotionReviewNotesDisplayItem[] {
  const byWindow = new Map<string, MotionReviewNotesDisplayItem>();

  payload.section.items.forEach((item) => {
    const record = reportWebRecordsByWindow[item.window_id];
    byWindow.set(item.window_id, {
      window_id: item.window_id,
      statement: item.statement,
      concept_display: item.concept_display,
      attack_area_display: item.attack_area_display,
      published_to_report: record?.published_to_report ?? false,
      visible_in_motion_review_notes: record?.visible_in_motion_review_notes ?? false,
      insertion_status: record?.insertion_status ?? 'customer_report_insertion_candidate',
    });
  });

  Object.values(reportWebRecordsByWindow).forEach((record) => {
    if (byWindow.has(record.window_id)) {
      return;
    }
    if (
      record.insertion_status === 'customer_report_inserted' ||
      record.insertion_status === 'customer_report_revoked'
    ) {
      byWindow.set(record.window_id, {
        window_id: record.window_id,
        statement: record.statement,
        concept_display: record.concept_display,
        attack_area_display: record.attack_area_display,
        published_to_report: record.published_to_report,
        visible_in_motion_review_notes: record.visible_in_motion_review_notes ?? false,
        insertion_status: record.insertion_status,
      });
    }
  });

  return Array.from(byWindow.values()).sort((left, right) =>
    left.window_id.localeCompare(right.window_id)
  );
}

export const MotionReviewNotesPreviewPanelView: React.FC<
  MotionReviewNotesPreviewPanelViewProps
> = ({
  uploadId,
  payload,
  displayItems,
  loading,
  error,
  insertedBy,
  onInsertedByChange,
  actionLoadingWindowId,
  cardSuccessMessages = {},
  cardErrorMessages = {},
  onPublishToReportWeb,
  onRevokeFromReportWeb,
}) => {
  const insertedByReady = normalizeReviewerId(insertedBy).length > 0;

  if (loading) {
    return (
      <section className="rounded-lg border border-secondary-200 p-4 text-sm text-secondary-600 dark:border-secondary-700 dark:text-secondary-300">
        Loading Motion Review Notes preview…
      </section>
    );
  }

  if (error) {
    return (
      <section
        className="rounded-lg border border-secondary-200 p-4 text-sm text-secondary-600 dark:border-secondary-700 dark:text-secondary-300"
        data-testid="motion-review-notes-preview-error"
      >
        {error}
      </section>
    );
  }

  if (!payload) {
    return null;
  }

  const hasItems = displayItems.length > 0;

  return (
    <section
      className="space-y-4 rounded-lg border border-secondary-200 p-4 dark:border-secondary-700"
      data-testid="motion-review-notes-preview-panel"
      data-upload-id={uploadId}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">
          Motion Review Notes Preview
        </h3>
        <InternalOnlyBadge />
        <NotPublishedBadge />
        <AdminPreviewBadge />
      </div>

      <p
        className="text-xs text-amber-900 dark:text-amber-200"
        data-testid="motion-review-notes-preview-warning"
      >
        This is an internal admin preview. It does not publish to the customer report, /full,
        Evidence Reel, delivery, exports, or commissioner surfaces.
      </p>

      <label className="block text-xs font-medium text-secondary-700 dark:text-secondary-200">
        Inserted by
        <input
          className="mt-1 w-full rounded border border-secondary-300 bg-white p-2 text-sm dark:border-secondary-600 dark:bg-secondary-900"
          value={insertedBy}
          onChange={(event) => onInsertedByChange(event.target.value)}
          placeholder="admin:antoine"
          data-testid="motion-review-notes-inserted-by"
        />
      </label>

      <div className="flex flex-wrap gap-2 text-xs">
        <span
          className="rounded bg-secondary-100 px-2 py-1 font-mono text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200"
          data-testid="preview-customer-visible"
        >
          customer_visible=false
        </span>
        <span
          className="rounded bg-secondary-100 px-2 py-1 font-mono text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200"
          data-testid="preview-published-to-report"
        >
          published_to_report=false
        </span>
        <span
          className="rounded bg-secondary-100 px-2 py-1 font-mono text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200"
          data-testid="preview-projection-mode"
        >
          {payload.projection_mode}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded bg-secondary-50 p-2 dark:bg-secondary-800/60">
          <div className="font-medium text-secondary-500 dark:text-secondary-400">Items</div>
          <div
            className="text-lg font-semibold text-secondary-900 dark:text-white"
            data-testid="preview-item-count"
          >
            {payload.summary.item_count}
          </div>
        </div>
        <div className="rounded bg-secondary-50 p-2 dark:bg-secondary-800/60">
          <div className="font-medium text-secondary-500 dark:text-secondary-400">Excluded</div>
          <div
            className="text-lg font-semibold text-secondary-900 dark:text-white"
            data-testid="preview-excluded-count"
          >
            {payload.summary.excluded_count}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <h4
          className="text-sm font-semibold text-secondary-900 dark:text-white"
          data-testid="preview-section-title"
        >
          {payload.section.title}
        </h4>
        <p
          className="text-xs text-secondary-600 dark:text-secondary-300"
          data-testid="preview-section-disclaimer"
        >
          {payload.section.disclaimer}
        </p>
      </div>

      {hasItems ? (
        <div className="space-y-2">
          {displayItems.map((item) => {
            const isInserted = item.insertion_status === 'customer_report_inserted';
            const isRevoked = item.insertion_status === 'customer_report_revoked';
            const cardBusy = actionLoadingWindowId === item.window_id;
            const canPublish =
              insertedByReady &&
              !isInserted &&
              !isRevoked &&
              item.insertion_status === 'customer_report_insertion_candidate';
            const canRevoke = insertedByReady && isInserted && !cardBusy;

            return (
              <article
                key={item.window_id}
                className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-700"
                data-testid={`preview-item-${item.window_id}`}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-secondary-500 dark:text-secondary-400">
                    {item.window_id}
                  </span>
                  <span className="rounded bg-secondary-100 px-1.5 py-0.5 font-mono text-[10px] text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200">
                    customer_visible=false
                  </span>
                  <span
                    className="rounded bg-secondary-100 px-1.5 py-0.5 font-mono text-[10px] text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200"
                    data-testid={`preview-item-published-to-report-${item.window_id}`}
                  >
                    published_to_report={String(item.published_to_report)}
                  </span>
                  <span
                    className="rounded bg-secondary-100 px-1.5 py-0.5 font-mono text-[10px] text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200"
                    data-testid={`preview-item-visible-in-notes-${item.window_id}`}
                  >
                    visible_in_motion_review_notes={String(item.visible_in_motion_review_notes)}
                  </span>
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                      isInserted
                        ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200'
                        : isRevoked
                          ? 'bg-orange-100 text-orange-900 dark:bg-orange-900/40 dark:text-orange-200'
                          : 'bg-sky-100 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200'
                    }`}
                    data-testid={`report-web-status-${item.window_id}`}
                  >
                    {reportWebStatusLabel(item.insertion_status)}
                  </span>
                </div>
                <p
                  className="mt-2 text-sm text-secondary-900 dark:text-white"
                  data-testid={`preview-statement-${item.window_id}`}
                >
                  {item.statement}
                </p>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-xs text-secondary-600 dark:text-secondary-300">
                  <div>
                    <dt className="font-medium text-secondary-500 dark:text-secondary-400">
                      Concept
                    </dt>
                    <dd data-testid={`preview-concept-${item.window_id}`}>{item.concept_display}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-secondary-500 dark:text-secondary-400">
                      Attack area
                    </dt>
                    <dd data-testid={`preview-attack-area-${item.window_id}`}>
                      {item.attack_area_display}
                    </dd>
                  </div>
                </dl>

                {cardSuccessMessages[item.window_id] ? (
                  <p
                    className="mt-3 rounded bg-emerald-50 p-2 text-xs text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200"
                    data-testid={`report-web-success-${item.window_id}`}
                  >
                    {cardSuccessMessages[item.window_id]}
                  </p>
                ) : null}

                {cardErrorMessages[item.window_id] ? (
                  <p
                    className="mt-3 rounded bg-red-50 p-2 text-xs text-red-900 dark:bg-red-900/20 dark:text-red-200"
                    data-testid={`report-web-error-${item.window_id}`}
                  >
                    {cardErrorMessages[item.window_id]}
                  </p>
                ) : null}

                <div className="mt-4 space-y-2 border-t border-secondary-200 pt-3 dark:border-secondary-700">
                  <p
                    className="text-xs text-amber-900 dark:text-amber-200"
                    data-testid={`report-web-warning-${item.window_id}`}
                  >
                    This publishes only to the feature-flagged Motion Review Notes web endpoint. It
                    does not modify /full, Evidence Reel, delivery, exports, or commissioner
                    surfaces. The customer endpoint remains disabled unless
                    MOTION_REVIEW_NOTES_CUSTOMER_ENABLED is enabled.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn-secondary text-xs disabled:opacity-50"
                      disabled={!canPublish || cardBusy}
                      data-testid={`publish-report-web-${item.window_id}`}
                      onClick={() => onPublishToReportWeb(item.window_id)}
                    >
                      Publish to Motion Review Notes web
                    </button>
                    <button
                      type="button"
                      className="btn-secondary text-xs disabled:opacity-50"
                      disabled={!canRevoke}
                      data-testid={`revoke-report-web-${item.window_id}`}
                      onClick={() => onRevokeFromReportWeb(item.window_id)}
                    >
                      Revoke from Motion Review Notes web
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p
          className="text-sm text-secondary-600 dark:text-secondary-300"
          data-testid="preview-empty-state"
        >
          No Motion Review Notes are ready for preview. Create an active customer report insertion
          candidate first.
        </p>
      )}
    </section>
  );
};

interface MotionReviewNotesPreviewPanelProps {
  uploadId: string | null | undefined;
}

function mapPreviewError(err: unknown): string {
  if (err instanceof AdminReviewApiError) {
    if (err.status === 403 || err.status === 401) {
      return 'Admin key is required to view Motion Review Notes preview.';
    }
    if (err.status === 404) {
      return 'Motion Review Notes preview is not available for this report.';
    }
    return 'Unable to load Motion Review Notes preview.';
  }
  if (err instanceof Error) {
    return 'Unable to load Motion Review Notes preview.';
  }
  return 'Unable to load Motion Review Notes preview.';
}

function mapReportWebActionError(err: unknown, windowId: string): string {
  if (err instanceof AdminReviewApiError) {
    if (err.status === 409) {
      return `Unable to update ${windowId}: report-web state conflict (${err.message}).`;
    }
    if (err.status === 404) {
      return `Unable to update ${windowId}: report insertion record not found.`;
    }
    if (err.status === 403 || err.status === 401) {
      return `Unable to update ${windowId}: admin key missing or invalid.`;
    }
    return `Unable to update ${windowId}: ${err.message}`;
  }
  if (err instanceof Error) {
    return `Unable to update ${windowId}: ${err.message}`;
  }
  return `Unable to update ${windowId}.`;
}

const MotionReviewNotesPreviewPanel: React.FC<MotionReviewNotesPreviewPanelProps> = ({
  uploadId,
}) => {
  const [payload, setPayload] = useState<MotionReviewNotesPreviewResponse | null>(null);
  const [reportWebRecordsByWindow, setReportWebRecordsByWindow] = useState<
    Record<string, MotionReportInsertionCandidate>
  >({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [insertedBy, setInsertedBy] = useState('');
  const [actionLoadingWindowId, setActionLoadingWindowId] = useState<string | null>(null);
  const [cardSuccessMessages, setCardSuccessMessages] = useState<Record<string, string>>({});
  const [cardErrorMessages, setCardErrorMessages] = useState<Record<string, string>>({});

  const loadPanelData = useCallback(async () => {
    if (!uploadId) return;
    setLoading(true);
    setError(null);
    try {
      const [previewResponse, insertionResponse] = await Promise.all([
        fetchMotionReviewNotesPreview(uploadId),
        fetchMotionClaimReportInsertionCandidates(uploadId),
      ]);
      setPayload(previewResponse);
      const recordsByWindow: Record<string, MotionReportInsertionCandidate> = {};
      insertionResponse.candidates.forEach((candidate) => {
        recordsByWindow[candidate.window_id] = candidate;
      });
      setReportWebRecordsByWindow((current) => {
        const next: Record<string, MotionReportInsertionCandidate> = { ...recordsByWindow };
        Object.entries(current).forEach(([windowId, record]) => {
          if (
            record.insertion_status === 'customer_report_inserted' ||
            record.insertion_status === 'customer_report_revoked'
          ) {
            next[windowId] = record;
          }
        });
        return next;
      });
    } catch (err: unknown) {
      setPayload(null);
      setReportWebRecordsByWindow({});
      setError(mapPreviewError(err));
    } finally {
      setLoading(false);
    }
  }, [uploadId]);

  useEffect(() => {
    if (!uploadId) {
      setPayload(null);
      setReportWebRecordsByWindow({});
      setError(null);
      setCardSuccessMessages({});
      setCardErrorMessages({});
      return;
    }
    loadPanelData();
  }, [uploadId, loadPanelData]);

  const handlePublishToReportWeb = useCallback(
    async (windowId: string) => {
      if (!uploadId || !normalizeReviewerId(insertedBy)) return;
      setActionLoadingWindowId(windowId);
      setCardErrorMessages((current) => {
        const next = { ...current };
        delete next[windowId];
        return next;
      });
      try {
        const response = await publishMotionReviewNoteToReport(uploadId, windowId, {
          inserted_by: insertedBy,
          publish_note: 'Publish to Motion Review Notes web report section only.',
        });
        setReportWebRecordsByWindow((current) => ({
          ...current,
          [windowId]: response.candidate,
        }));
        setCardSuccessMessages((current) => ({
          ...current,
          [windowId]: `Updated ${windowId}: published to Motion Review Notes web report section.`,
        }));
        await loadPanelData();
      } catch (err: unknown) {
        setCardErrorMessages((current) => ({
          ...current,
          [windowId]: mapReportWebActionError(err, windowId),
        }));
      } finally {
        setActionLoadingWindowId(null);
      }
    },
    [uploadId, insertedBy, loadPanelData]
  );

  const handleRevokeFromReportWeb = useCallback(
    async (windowId: string) => {
      if (!uploadId || !normalizeReviewerId(insertedBy)) return;
      setActionLoadingWindowId(windowId);
      setCardErrorMessages((current) => {
        const next = { ...current };
        delete next[windowId];
        return next;
      });
      try {
        const response = await revokeMotionReviewNoteFromReport(uploadId, windowId, {
          inserted_by: insertedBy,
          revoke_reason: 'Remove from Motion Review Notes web report section.',
        });
        setReportWebRecordsByWindow((current) => ({
          ...current,
          [windowId]: response.candidate,
        }));
        setCardSuccessMessages((current) => ({
          ...current,
          [windowId]: `Updated ${windowId}: revoked from Motion Review Notes web report section.`,
        }));
        await loadPanelData();
      } catch (err: unknown) {
        setCardErrorMessages((current) => ({
          ...current,
          [windowId]: mapReportWebActionError(err, windowId),
        }));
      } finally {
        setActionLoadingWindowId(null);
      }
    },
    [uploadId, insertedBy, loadPanelData]
  );

  if (!uploadId) {
    return null;
  }

  const displayItems = payload
    ? buildMotionReviewNotesDisplayItems(payload, reportWebRecordsByWindow)
    : [];

  return (
    <MotionReviewNotesPreviewPanelView
      uploadId={uploadId}
      payload={payload}
      displayItems={displayItems}
      loading={loading}
      error={error}
      insertedBy={insertedBy}
      onInsertedByChange={setInsertedBy}
      actionLoadingWindowId={actionLoadingWindowId}
      cardSuccessMessages={cardSuccessMessages}
      cardErrorMessages={cardErrorMessages}
      onPublishToReportWeb={handlePublishToReportWeb}
      onRevokeFromReportWeb={handleRevokeFromReportWeb}
    />
  );
};

export default MotionReviewNotesPreviewPanel;
